import type {
  ClientRect,
  Modifier
} from '@dnd-kit/core';
import type {
  Transform
} from '@dnd-kit/utilities';

function restrictToBoundingRect(
  transform: Transform,
  rect: ClientRect,
  boundingRect: ClientRect,
  padding = 0
): Transform {
  const value = {
    ...transform,
  };

  const paddedRect = {
    top: boundingRect.top + padding,
    bottom: boundingRect.bottom - padding,
    left: boundingRect.left + padding,
    right: boundingRect.right - padding,
    width: boundingRect.width - padding * 2,
    height: boundingRect.height - padding * 2,
  };

  if (rect.top + transform.y <= paddedRect.top) {
    value.y = paddedRect.top - rect.top;
  } else if (rect.bottom + transform.y >= paddedRect.bottom) {
    value.y = paddedRect.bottom - rect.bottom;
  }

  if (rect.left + transform.x <= paddedRect.left) {
    value.x = paddedRect.left - rect.left;
  } else if (rect.right + transform.x >= paddedRect.right) {
    value.x = paddedRect.right - rect.right;
  }

  return value;
}

/**
 * Keeps a dragged node inside the area its own page scrolls within, rather than inside the window.
 *
 * The window is the wrong bound for an item being reordered inside a page: a page that owns its
 * scrolling is shorter than the window — it sits under the header and above the footer — so a card
 * bounded only by the window can still be dragged clear of every other card, where there is nothing
 * to drop it on, and once the page scrolls under the drag there is no way back (#5141).
 *
 * dnd-kit measures the scrollable ancestors on every move, so this bound follows the page as it
 * scrolls without anything being captured or re-read from a ref. The window is the fallback for a
 * draggable that has no scrolling ancestor at all.
 */
export function restrictToScrollAreaWithPadding(padding = 0): Modifier {
  return ({transform, draggingNodeRect, scrollableAncestorRects, windowRect}) => {
    const bounds = scrollableAncestorRects[0] ?? windowRect;
    if (!draggingNodeRect || !bounds) {
      return transform;
    }

    return restrictToBoundingRect(transform, draggingNodeRect, bounds, padding);
  };
}
